// src/components/sections/research.js

import React from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';
import { useReveal } from '@hooks';
import { travellingRing } from '@styles/glow';
import { COLLAPSED_HEIGHT } from '@components/chatpanel';
import { Display } from '@components/research';

/* The home page's entry to the research, and nothing more than that.
   ------------------------------------------------------------------------
   Its whole job is to make a reader believe there is a specific thesis here
   and click through. The argument itself lives on /research, which is also
   written to be read on its own by someone who never saw this section.

   That split is the rule this section is held to. A claim belongs here. The
   example that supports it, the mechanism behind it and its consequences
   belong on /research, which has the callouts and figures to present an
   example properly. When the two pages carried the same paragraphs, a reader
   who clicked through read them twice and concluded there was nothing more.

   Every card carries the same three registers, and the size ladder between
   them is what lets the section be read at three depths without a control to
   click: the claim at 18px, the support at 16px, what current AI does instead
   at 14px under a label. A reader can take only the four claim lines, or only
   the four counters, and get a complete pass through the section either way.

   No card opens with a story. A reader who has not yet decided the section is
   worth their attention will not work out the point of an anecdote, and four
   anecdotes at four different lengths make the block look unconsidered before
   a word of it is read. */
const StyledResearchSection = styled.section`
  .intro {
    max-width: 42rem;
    margin: 0 auto var(--space-7);
    color: var(--text-secondary);
    font-size: var(--fz-md);
    line-height: var(--lh-body);
    text-align: center;

    strong {
      color: var(--text-emphasis);
      font-weight: 500;
    }
  }

  .label {
    display: block;
    margin-bottom: var(--space-5);
    color: var(--text-muted);
    font-family: var(--font-sans);
    font-size: var(--fz-xs);
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
  }

  .cards {
    ${({ theme }) => theme.mixins.resetList};
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-gap: var(--space-6) var(--space-7);

    @media (max-width: 850px) {
      grid-template-columns: 1fr;
      grid-gap: var(--space-6);
    }
  }

  .card {
    padding-top: var(--space-4);
    border-top: 1px solid var(--border);
    transition: var(--transition);

    /* The card's body size lives on the card rather than on its paragraphs.
       Everything inside that wants a different size states one, so this only
       decides what an unstyled block inherits, and any block added later
       inherits the card's register instead of the page's. */
    font-size: var(--fz-md);

    &:hover {
      border-top-color: var(--accent);
    }

    h3 {
      margin: 0 0 var(--space-3);
      color: var(--text);
      font-size: var(--fz-xxl);
      font-weight: 500;
      line-height: var(--lh-heading);
      letter-spacing: -0.01em;
    }

    /* One step below the site's --fz-lg body copy, and the only prose on the
       site set that way. It is a deliberate register rather than an oversight:
       in a two column grid, 18px reads as a page of prose that has been cut
       into boxes, and 16px reads as a card. The size itself is set on .card
       above; this only carries what is specific to a paragraph. */
    p {
      margin: 0 0 var(--space-3);
      color: var(--text-body);
      line-height: var(--lh-body);

      &:last-child {
        margin-bottom: 0;
      }
    }

    /* The claim, lifted out of the paragraph onto its own line. This replaced
       an emphasis span inside the body: the same sentence was already carrying
       the card, and inside a paragraph it could only be found by reading the
       paragraph, which is the thing a reader deciding whether to care will not
       do. */
    .lede {
      color: var(--text-secondary);
      font-size: var(--fz-lg);
      font-weight: 500;
      line-height: var(--lh-heading);
    }

    /* What current AI does instead. Marked as a fixture of the card rather
       than as more prose, so it never has to compete with the body for a
       reader who is scanning. */
    .counter {
      display: grid;
      grid-template-columns: auto 1fr;
      grid-gap: var(--space-3);
      align-items: start;
      padding-top: var(--space-1);

      .tag {
        /* Nudged onto the value's first baseline: the label is two steps
           smaller, so aligning the two line boxes leaves it riding high. */
        padding-top: 0.1em;
        color: var(--accent);
        font-family: var(--font-sans);
        font-size: var(--fz-xxs);
        font-weight: 600;
        letter-spacing: 0.12em;
        line-height: var(--lh-body);
        text-transform: uppercase;
        white-space: nowrap;
      }

      p {
        margin: 0;
        color: var(--text-muted);
        font-size: var(--fz-sm);
      }
    }
  }

  .cta {
    display: flex;
    justify-content: center;
    margin-top: var(--space-8);
  }

  /* The same travelling ring the collapsed chat panel carries, so the two
     resting calls to action on the site read as one idiom. It belongs to the
     resting state and fades out on hover, where the solid accent border takes
     over. */
  .cta-button {
    ${({ theme }) => theme.mixins.bigButton};
    ${travellingRing};
    /* Same mixin and same fixed height as the collapsed chat panel, so the
       two ringed buttons on the site are the same object at a glance. */
    height: ${COLLAPSED_HEIGHT};
    font-size: var(--fz-lg);
  }
`;

const QUESTION = '“What is intelligence, actually?”';
const SUB = 'Everyone is building toward it. Nobody knows what they are building toward.';

const Research = () => {
  const revealContainer = useReveal();

  return (
    <StyledResearchSection id="research" ref={revealContainer}>
      <h2 className="numbered-heading">The Question I&rsquo;ve Been Working On Since I Was 16</h2>

      <Display sub={SUB}>{QUESTION}</Display>

      <div className="intro">
        <p>
          I started this work out of curiosity about psychology and philosophy, before I knew it had
          anything to do with AI. When I studied different schools of thought, I found that all of
          them held themselves to be correct, but they contradicted one another. Rather than adopt
          one, I wondered what sits underneath all of them.
        </p>

        <p>
          I realized that{' '}
          <strong>
            different thoughts are just structure held inside a human mind as an instance of
            cognition
          </strong>
          , and I see intelligence as the process of approaching a more general structure that can
          contain different thoughts rather than collapsing into one. The core of this work is to
          determine what human intelligence actually is, and to build a{' '}
          <strong>mathematical foundation</strong> for&nbsp;it.
        </p>
      </div>

      {/*
      

      <span className="label">Four differences between human intelligence and current AI</span>

      <ul className="cards">
        <li className="card">
          <h3>Reasoning Compresses Into Reaction</h3>
          <p className="lede">Our mind can turn a long-chain reasoning into a direct reaction.</p>
          <p>
            Once that reasoning chain has been traversed often enough, the reaction arrives on its
            own, and the intermediate steps are no longer effectively involved in producing it. The
            capacity they occupied is also freed to accommodate new knowledge. Such compression
            appears as intuition, skill automaticity, curse of knowledge, and expert chunking.
          </p>
          <div className="counter">
            <span className="tag">Current AI</span>
            <p>
              The topology of a model is fixed before training and only its parameters are updated,
              so a long path traversed many times never becomes a short one.
            </p>
          </div>
        </li>

        <li className="card">
          <h3>Feedback Is Learned, Not Given</h3>
          <p className="lede">What counts as good or bad is itself learned, and not universal.</p>
          <p>
            The same event can register as good to one person but bad to another, and it changes the
            later behavior of the two of them in different directions. Only a small set of primitive
            interoceptive signals are given at birth, and everything else a person reacts to as good
            or bad is psychology built on top of those through experience.
          </p>
          <div className="counter">
            <span className="tag">Current AI</span>
            <p>
              Almost every current training scheme takes the judgement of good and bad from outside
              and holds it fixed: a target in supervised learning, a reward function in
              reinforcement learning.
            </p>
          </div>
        </li>

        <li className="card">
          <h3>Thinking Adds Intelligence Without Data</h3>
          <p className="lede">Humans can become smarter by thinking without any new data.</p>
          <p>
            Thinking in the human mind restructures the connections between concepts, and gives you
            a deeper understanding of a topic than you had before, which is an increase in
            intelligence. In this process, there is no information flow across the boundary of the
            system. Thinking does not require taking external input or producing output; it can be
            entirely internal processing within a system of a structure that is already present.
          </p>
          <div className="counter">
            <span className="tag">Current AI</span>
            <p>
              Current models buy intelligence with more data. Test-time training and training on
              generated data are no exception: data still crosses the boundary of the system.
            </p>
          </div>
        </li>

        <li className="card">
          <h3>Supervised Learning Is Not Primitive</h3>
          <p className="lede">Supervised learning is primitive in AI, but not in humans.</p>
          <p>
            A child cannot learn in the supervised sense until they can recognize the concept
            &ldquo;my answer is different from the one I was given&rdquo; and have linked that
            recognized concept to a negative feedback. Both of these abilities take years of
            cognitive development to form. So in a human, the ability to learn from supervision is
            itself a learned structure rather than something hardcoded.
          </p>
          <div className="counter">
            <span className="tag">Current AI</span>
            <p>
              A supervised learning model takes the ability for granted. Its learning procedure is
              intrinsically constructed to chase after the given targets, not acquired by the model.
            </p>
          </div>
        </li>
      </ul>

      */}

      <div className="cta">
        <Link className="cta-button" to="/research">
          Read the research brief
        </Link>
      </div>
    </StyledResearchSection>
  );
};

export default Research;
